/**
 * Fill tab (A7Y-4883): how fast the corpus is filling.
 *
 * Recent hourly arrivals (stacked), the daily table with a running total
 * that ends at the headline, and the day-30 projection. Rows from
 * `apple_ads.search_term_popularity` are dated to their Apple list week, so
 * they are stacked and tabulated separately and the dating is stated above
 * both (D17).
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import { KEYWORD_DATABASE_FILL_DAYS } from '@a7y/types';
import type { KeywordDatabaseFill, KeywordDatabaseFillDays } from '@a7y/types';
import { useTheme } from '@/contexts/useTheme';
import { formatInt } from '../format';

const COLORS = {
  light: {
    arrivals: '#3b82f6',
    listWeek: '#f59e0b',
    play: '#10b981',
    actual: '#3b82f6',
    ref: '#71717a',
  },
  dark: {
    arrivals: '#3987e5',
    listWeek: '#c98500',
    play: '#199e70',
    actual: '#3987e5',
    ref: '#a1a1aa',
  },
} as const;

const DAY_MS = 86_400_000;

function utcMs(day: string): number {
  return Date.parse(`${day}T00:00:00Z`);
}

function shortDay(ms: number): string {
  return new Date(ms).toISOString().slice(5, 10);
}

function DaysToMillion({ fill }: { fill: KeywordDatabaseFill }) {
  let text: string;
  if (fill.daysTo1M !== null) {
    text = `${fill.daysTo1M.toFixed(1)} days`;
  } else if (fill.rate7d <= 0) {
    text = 'no growth in the last 7 days';
  } else {
    text = '1,000,000 already reached';
  }
  return (
    <div
      data-testid="days-to-1m"
      className="rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700"
    >
      <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">Days to 1,000,000</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-zinc-950 dark:text-white">
        {text}
      </p>
      <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
        at {formatInt(fill.rate7d)} new per day (7-day average)
      </p>
    </div>
  );
}

export function FillTab({
  fill,
  days,
  onDaysChange,
}: {
  fill: KeywordDatabaseFill;
  days: KeywordDatabaseFillDays;
  onDaysChange: (days: KeywordDatabaseFillDays) => void;
}) {
  const { resolvedTheme } = useTheme();
  const c = COLORS[resolvedTheme === 'dark' ? 'dark' : 'light'];
  const axisTick = { fontSize: 11, fill: '#9ca3af' };
  const gridStroke = resolvedTheme === 'dark' ? '#3f3f46' : '#e4e4e7';

  const hourly = fill.hourly48h.map((h) => ({
    hour: h.hour,
    arrivals: h.appstore - h.listWeekDated,
    listWeek: h.listWeekDated,
    play: h.playstore,
  }));
  const dailyNewestFirst = [...fill.daily].reverse();

  const { projection } = fill;
  const t0 = utcMs(projection.day0);
  const t30 = utcMs(projection.day30);
  const day0Row = fill.daily.find((d) => d.day === projection.day0);
  const actual = fill.daily
    .filter((d) => d.day >= projection.day0)
    .map((d) => ({ t: utcMs(d.day), actual: d.runningTotal }));
  const projectionData = day0Row
    ? [
        ...actual.map((p) =>
          p.t === t0
            ? {
                ...p,
                low: day0Row.runningTotal,
                central: day0Row.runningTotal,
                high: day0Row.runningTotal,
              }
            : p
        ),
        { t: t30, low: projection.low, central: projection.central, high: projection.high },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p
          data-testid="fill-list-week-note"
          className="max-w-3xl text-xs text-zinc-600 dark:text-zinc-300"
        >
          Rows from apple_ads.search_term_popularity are dated to their Apple list week (Saturday
          00:00Z), not to when they arrived (A7Y-4785); they are counted in every total and shown
          separately here.
        </p>
        <div className="flex items-center gap-1">
          {KEYWORD_DATABASE_FILL_DAYS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDaysChange(d)}
              aria-pressed={d === days}
              className={`rounded px-2 py-1 text-xs border transition-colors ${
                d === days
                  ? 'border-zinc-400 dark:border-zinc-500 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium'
                  : 'border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
        <h2 className="mb-3 text-sm font-medium text-zinc-900 dark:text-zinc-100">
          New keywords per hour (last 48 h, UTC)
        </h2>
        <div className="h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={hourly} margin={{ top: 8, right: 16, bottom: 0, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
              <XAxis
                dataKey="hour"
                tick={axisTick}
                tickLine={false}
                minTickGap={24}
                tickFormatter={(h: string) => `${h.slice(8, 10)} ${h.slice(11, 13)}h`}
              />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} width={48} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="arrivals"
                name="App Store arrivals"
                stackId="h"
                fill={c.arrivals}
                isAnimationActive={false}
              />
              <Bar
                dataKey="listWeek"
                name="App Store list-week-dated"
                stackId="h"
                fill={c.listWeek}
                isAnimationActive={false}
              />
              <Bar dataKey="play" name="Play" stackId="h" fill={c.play} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
        <section className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
          <h2 className="mb-1 text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Running total and day-30 projection
          </h2>
          {projectionData.length > 0 ? (
            <>
              <p
                data-testid="fill-projection-legend"
                className="mb-3 text-xs text-zinc-500 dark:text-zinc-400"
              >
                Dashed lines are a straight-line reference from the day-0 running total to the low,
                central and high figures at {projection.day30} ({formatInt(projection.low)} /{' '}
                {formatInt(projection.central)} / {formatInt(projection.high)}; source{' '}
                {projection.source}).
              </p>
              <div className="h-[240px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={projectionData}
                    margin={{ top: 8, right: 24, bottom: 0, left: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
                    <XAxis
                      dataKey="t"
                      type="number"
                      scale="time"
                      domain={[t0, t30]}
                      tick={axisTick}
                      tickLine={false}
                      tickFormatter={shortDay}
                    />
                    <YAxis
                      tick={axisTick}
                      axisLine={false}
                      tickLine={false}
                      width={64}
                      tickFormatter={formatInt}
                    />
                    <Tooltip
                      labelFormatter={(t: number) => new Date(t).toISOString().slice(0, 10)}
                      formatter={(v: number) => formatInt(v)}
                    />
                    <ReferenceArea
                      x1={t30 - DAY_MS}
                      x2={t30}
                      y1={projection.low}
                      y2={projection.high}
                      fill={c.ref}
                      fillOpacity={0.2}
                    />
                    <ReferenceDot x={t30} y={projection.central} r={4} fill={c.ref} stroke="none" />
                    <Line
                      dataKey="actual"
                      name="Running total"
                      stroke={c.actual}
                      strokeWidth={2}
                      dot={{ r: 2.5 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                    <Line
                      dataKey="low"
                      name="Low (straight-line reference)"
                      stroke={c.ref}
                      strokeDasharray="4 4"
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                    <Line
                      dataKey="central"
                      name="Central (straight-line reference)"
                      stroke={c.ref}
                      strokeDasharray="4 4"
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                    <Line
                      dataKey="high"
                      name="High (straight-line reference)"
                      stroke={c.ref}
                      strokeDasharray="4 4"
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <p
              data-testid="fill-projection-legend"
              className="text-xs text-zinc-500 dark:text-zinc-400"
            >
              The projection starts at day 0 ({projection.day0}), which is outside this window;
              choose a longer window to see it.
            </p>
          )}
        </section>
        <DaysToMillion fill={fill} />
      </div>

      <Table dense striped data-testid="fill-daily-table">
        <TableHead>
          <TableRow>
            <TableHeader>Day (UTC)</TableHeader>
            <TableHeader>App Store</TableHeader>
            <TableHeader>of which list-week-dated</TableHeader>
            <TableHeader>Play</TableHeader>
            <TableHeader>Total</TableHeader>
            <TableHeader>Running total</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {dailyNewestFirst.map((d) => (
            <TableRow key={d.day} data-testid={`fill-row-${d.day}`}>
              <TableCell className="whitespace-nowrap font-medium">{d.day}</TableCell>
              <TableCell className="tabular-nums">{formatInt(d.appstore)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(d.listWeekDated)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(d.playstore)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(d.total)}</TableCell>
              <TableCell className="tabular-nums">{formatInt(d.runningTotal)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
